// Commercial contacts — NEW console page (18H §4.2 / 17_ §5d). The account
// channel: admin/billing/content_owner contacts, authoritative HERE, pushed
// to TPRM7 for display/invite defaults. Read + a manual per-license "push"
// (re-send this license's contacts, e.g. after an operator edit upstream).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { RefreshCw, UploadCloud, AlertTriangle } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { personaBase } from '@/lib/persona';
import { Kpi } from '@/components/shared/Kpi';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { useToast } from '@/components/shared/ToastProvider';
import { getContacts, pushContacts, type CommercialContactRow, type CommercialContactRole } from '@/lib/api/trust';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13 };

const ROLE_LABEL: Record<CommercialContactRole, string> = { admin: 'Admin', billing: 'Billing', content_owner: 'Content owner' };
const ROLE_TINT: Record<CommercialContactRole, { bg: string; fg: string }> = {
  admin: { bg: 'var(--ps-pill-blue-bg)', fg: 'var(--ps-pill-blue-tx)' },
  billing: { bg: 'var(--ps-pill-green-bg)', fg: 'var(--ps-pill-green-tx)' },
  content_owner: { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)' },
};

const SYNC_STALE_MS = 30 * 24 * 3600 * 1000; // >30d since last TPRM7 contact-sync reconcile

function SyncFreshness({ syncedAt }: { syncedAt: string | null }) {
  if (!syncedAt) return <span style={{ color: 'var(--text-muted)' }}>Never synced</span>;
  const stale = Date.now() - new Date(syncedAt).getTime() > SYNC_STALE_MS;
  return (
    <span style={{ color: stale ? '#D97706' : 'var(--text-secondary)', fontWeight: stale ? 600 : 400 }}>
      {formatRelativeTime(syncedAt)}{stale ? ' (stale)' : ''}
    </span>
  );
}

export function CommercialContactsPage() {
  const location = useLocation();
  const base = personaBase(location.pathname);
  const { toast } = useToast();
  const [rows, setRows] = useState<CommercialContactRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<CommercialContactRole | ''>('');
  const [search, setSearch] = useState('');
  const [pushingId, setPushingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const data = await getContacts();
      setRows(data || []);
      setState('ready');
    } catch (err) {
      setRows([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (roleFilter && r.role !== roleFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      const hay = `${r.name || ''} ${r.email} ${r.license?.organization?.name || ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [rows, roleFilter, search]);

  const kpis = useMemo(() => {
    const drifting = rows.filter((r) => r.drift_detected).length;
    const neverSynced = rows.filter((r) => !r.synced_at).length;
    const byRole = { admin: 0, billing: 0, content_owner: 0 } as Record<CommercialContactRole, number>;
    rows.forEach((r) => { byRole[r.role] = (byRole[r.role] || 0) + 1; });
    return { total: rows.length, drifting, neverSynced, byRole };
  }, [rows]);

  const doPush = async (licenseId: string) => {
    setPushingId(licenseId);
    try {
      await pushContacts(licenseId);
      toast({ kind: 'success', title: 'Contacts push queued.' });
      await load();
    } catch (err) {
      toast({ kind: 'error', title: 'Push failed', body: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setPushingId(null);
    }
  };

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>{[0, 1, 2, 3, 4].map((i) => <TableRowSkeleton key={i} cols={[120, 140, 70, 90, 80, 70]} />)}</tbody>
        </table>
      </div>
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Commercial contacts</h1>
          <p className="page-subtitle">The account channel (17_ §5d) — authoritative here, pushed to TPRM7 for display/invite defaults.</p>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={rows.length === 0}
        emptyIcon="contact"
        emptyTitle="No commercial contacts yet"
        emptyBody="Contacts are written at trust-org provisioning and updated by TPRM7's nightly contact-sync reconcile. This fills in once a saas org has been provisioned."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          <Kpi label="Total contacts" value={kpis.total} icon="contact" />
          <Kpi label="Admin" value={kpis.byRole.admin} icon="shield" />
          <Kpi label="Billing" value={kpis.byRole.billing} icon="credit-card" />
          <Kpi label="Content owner" value={kpis.byRole.content_owner} icon="file-edit" />
        </div>

        {kpis.drifting > 0 && (
          <div style={{ padding: '10px 12px', background: 'var(--ps-pill-amber-bg)', borderRadius: 8, marginBottom: 12, fontSize: 13, color: 'var(--ps-pill-amber-tx)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={14} /> {kpis.drifting} contact{kpis.drifting !== 1 ? 's' : ''} flagged with drift by the last TPRM7 reconcile.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Search name, email, org…" value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none' }}
          />
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as CommercialContactRole | '')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)' }}>
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="billing">Billing</option>
            <option value="content_owner">Content owner</option>
          </select>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <th style={th}>Org</th>
                  <th style={th}>Contact</th>
                  <th style={th}>Role</th>
                  <th style={th}>Source</th>
                  <th style={th}>Sync freshness</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No contacts match your filters.</td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={td}>
                      {r.license?.id
                        ? <Link to={`${base}/licenses/${r.license.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>{r.license.organization?.name || r.license.license_id}</Link>
                        : '—'}
                    </td>
                    {/* ps-mask: rrweb maskTextClass (A20). This cell is a named
                        person at a customer org, synced from their tenant — the
                        commercial contact roster. The account name in the
                        previous cell is deliberately NOT masked: it is the
                        company, not a human, and it is what makes a bug report
                        about this table actionable at all. */}
                    <td style={td} className="ps-mask">
                      <div style={{ fontWeight: 500 }}>{r.name || '—'}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{r.email}</div>
                    </td>
                    <td style={td}>
                      <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: ROLE_TINT[r.role].bg, color: ROLE_TINT[r.role].fg }}>
                        {ROLE_LABEL[r.role]}
                      </span>
                    </td>
                    <td style={{ ...td, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{(r.source || '—').replace(/_/g, ' ')}</td>
                    <td style={td}>
                      <SyncFreshness syncedAt={r.synced_at} />
                      {r.drift_detected && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2 }}>Drift detected</div>}
                    </td>
                    <td style={td}>
                      <button
                        onClick={() => doPush(r.license_id)}
                        disabled={pushingId === r.license_id}
                        title="Push this license's contacts to TPRM7"
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: pushingId === r.license_id ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}
                      >
                        <UploadCloud size={12} /> {pushingId === r.license_id ? 'Pushing…' : 'Push'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </StateFrame>
    </div>
  );
}

export default CommercialContactsPage;
